/**
 * +acolyte-specs (S10, docs/design/classes/specs.md "Sim features"): damage share.
 *
 * While a unit carries an aura with `damageShare` (the Hemomancer's Martyr), `pct` of the damage
 * hostiles deal to its allies within `radius` (after mitigation, before their absorbs) is taken by
 * the holder instead: raw HP loss that never kills (like self-harm, so it still feeds missing-health
 * bonuses and damage-taken rules). The instance remembers what it took; when the aura ends the
 * holder heals the allies in range for `healBackPct` × that total, split evenly, through the normal
 * heal pipeline (the holder's healing mods apply: a Hemomancer deep in the red heals harder).
 */
import type { AuraDef, AuraInstance, DamageRequest, SimContext, Unit } from '../contracts';
import { dealDamage, heal } from './damage';
import { isHostile, unitOf } from './util';

const near = (a: Unit, b: Unit, r: number): boolean => Math.hypot(a.pos.x - b.pos.x, a.pos.y - b.pos.y) <= r;

/**
 * The part of `amount` (mitigated damage about to land on `t`) that stays on `t`; the rest is dealt
 * to the nearest damage-share holder in range.
 */
export function redirectShare(ctx: SimContext, t: Unit, amount: number, req: DamageRequest, depth: number): number {
  if (amount <= 0 || req.selfHarm || t.faction !== 'party' || t.pet) return amount;
  const s = unitOf(ctx, req.sourceId);
  if (!s || !isHostile(s, t)) return amount;
  for (const id of ctx.world.unitOrder) {
    const h = unitOf(ctx, id);
    if (!h?.alive || h.id === t.id || h.faction !== t.faction) continue;
    for (const inst of h.auras) {
      const ds = ctx.content.aura(inst.auraId)?.damageShare;
      if (!ds || !near(h, t, ds.radius)) continue;
      const share = Math.round(amount * ds.pct);
      if (share <= 0) return amount;
      inst.shared = (inst.shared ?? 0) + share;
      dealDamage(ctx, { sourceId: h.id, targetId: h.id, abilityId: inst.auraId, school: req.school, amount: share, selfHarm: true }, { depth: depth + 1 });
      return amount - share;
    }
  }
  return amount;
}

/** The share aura ended: heal the allies in range for what the holder took (removeInstance hook). */
export function payShare(ctx: SimContext, holder: Unit, inst: AuraInstance, def: AuraDef): void {
  const ds = def.damageShare;
  if (!ds || !inst.shared || !holder.alive) return;
  const total = inst.shared * (ds.healBackPct ?? 1);
  inst.shared = 0;
  const allies: Unit[] = [];
  for (const id of ctx.world.partyIds) {
    const u = unitOf(ctx, id);
    if (u?.alive && u.id !== holder.id && near(holder, u, ds.radius)) allies.push(u);
  }
  if (allies.length === 0 || total <= 0) return;
  for (const u of allies) heal(ctx, { sourceId: holder.id, targetId: u.id, abilityId: def.id, amount: total / allies.length, canCrit: false }, { depth: 1 });
}
